// src/services/brandServiceNew.js
import { api } from './api';
import { objectToQueryString } from '../utils/apiUtils';
import {
  createService,
  createFileUploadConfig,
  createNestedCollection
} from './factory';

// Create the base brand service using the factory
const brandServiceBase = createService('Brand', '/brands', {
  fileUploadOptions: createFileUploadConfig('file', 'logo'),
  nestedCollections: [
    createNestedCollection('assets', 'asset'),
    createNestedCollection('colors'),
    createNestedCollection('fonts'),
    createNestedCollection('voice'),
    createNestedCollection('socialAccounts', 'socialAccount'),
    createNestedCollection('competitors', 'competitor'),
    createNestedCollection('content'),
    createNestedCollection('campaigns', 'campaign'),
    createNestedCollection('analytics'),
  ],
  customEndpoints: {
    // Get brand usage statistics
    getBrandUsageStats: async (brandId) => {
      return api.get(`/brands/${brandId}/usage-stats`);
    },
    // Get brand guidelines PDF
    getBrandGuidelinesPdf: async (brandId) => {
      return api.get(`/brands/${brandId}/guidelines`, {
        responseType: 'blob'
      });
    }
  }
});

// Create backwards-compatible exports that use the factory methods underneath
export const getBrands = async (params = {}) => {
  return brandServiceBase.getAll(params);
};

export const getClientBrands = async (clientId, params = {}) => {
  // Add clientId to the params object
  const updatedParams = { ...params, clientId };
  return brandServiceBase.getAll(updatedParams);
};

export const getBrand = async (brandId) => {
  return brandServiceBase.getById(brandId);
};

export const createBrand = async (brandData) => {
  return brandServiceBase.create(brandData);
};

export const updateBrand = async (brandId, brandData) => {
  return brandServiceBase.update(brandId, brandData);
};

export const deleteBrand = async (brandId) => {
  return brandServiceBase.delete(brandId);
};

// Custom file upload methods to match the original API
export const uploadBrandLogo = async (brandId, logoType, logoFile) => {
  const formData = new FormData();
  formData.append('file', logoFile);
  formData.append('key', logoType);
  
  return await api.postFiles(`/brands/${brandId}/logo`, formData);
};

export const uploadBrandAsset = async (brandId, assetFile, metadata = {}) => {
  const formData = new FormData();
  formData.append('file', assetFile);
  
  // Add metadata
  if (metadata.category) formData.append('category', metadata.category);
  if (metadata.tags && metadata.tags.length > 0) {
    formData.append('tags', metadata.tags.join(','));
  }
  
  return await api.postFiles(`/brands/${brandId}/assets`, formData);
};

// Export brand asset methods
export const getBrandAssets = async (brandId, params = {}) => {
  return brandServiceBase.getAssets(brandId, params);
};

export const deleteBrandAsset = async (brandId, assetId) => {
  return brandServiceBase.removeAsset(brandId, assetId);
};

// Brand colors methods
export const getBrandColors = async (brandId) => {
  return brandServiceBase.getColors(brandId);
};

export const updateBrandColors = async (brandId, colors) => {
  return api.put(`/brands/${brandId}/colors`, { colors });
};

// Brand fonts methods
export const getBrandFonts = async (brandId) => {
  return brandServiceBase.getFonts(brandId);
};

export const updateBrandFonts = async (brandId, fonts) => {
  return api.put(`/brands/${brandId}/fonts`, { fonts });
};

// Brand voice methods
export const getBrandVoice = async (brandId) => {
  return brandServiceBase.getVoice(brandId);
};

export const updateBrandVoice = async (brandId, voiceData) => {
  return api.put(`/brands/${brandId}/voice`, { voice: voiceData });
};

// Brand social accounts methods
export const getBrandSocialAccounts = async (brandId) => {
  return brandServiceBase.getSocialAccounts(brandId);
};

export const updateBrandSocialAccounts = async (brandId, socialAccounts) => {
  return api.put(`/brands/${brandId}/social-accounts`, { socialAccounts });
};

// Brand competitors methods
export const getBrandCompetitors = async (brandId) => {
  return brandServiceBase.getCompetitors(brandId);
};

export const updateBrandCompetitors = async (brandId, competitors) => {
  return api.put(`/brands/${brandId}/competitors`, { competitors });
};

// Brand content methods
export const getBrandContent = async (brandId, params = {}) => {
  return brandServiceBase.getContent(brandId, params);
};

// Brand campaigns methods
export const getBrandCampaigns = async (brandId, params = {}) => {
  return brandServiceBase.getCampaigns(brandId, params);
};

// Brand analytics methods
export const getBrandAnalytics = async (brandId, params = {}) => {
  return brandServiceBase.getAnalytics(brandId, params);
};

// Brand usage statistics
export const getBrandUsageStats = async (brandId) => {
  return brandServiceBase.getBrandUsageStats(brandId);
};

// Brand guidelines PDF
export const getBrandGuidelinesPdf = async (brandId) => {
  return brandServiceBase.getBrandGuidelinesPdf(brandId);
};